// /orgs/$orgSlug/projects/$projectId/shots/$shotId/lineage/$generationId
// — full lineage graph page (PRD §9.8, Stream B5 PR5 / B5e closing
// the stream).
//
// Owns the URL ↔ selected-edge bridge:
//   * `validateSearch` parses `?a=...&b=...` into a typed `LineageSearch`.
//     Both are optional; either both present or both absent — a single
//     half is treated as "no edge selected".
//   * Edge clicks in the LineageView call `onSelectEdge([from, to])`,
//     which `useNavigate({search})` flushes to the URL with
//     `replace: true` so each click doesn't accrete a history entry.
//   * The Close (×) on the diff panel clears both keys.

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';

import { ApiError, apiErrorMessage } from '../api/client';
import { LineageView } from '../components/LineageView';

interface LineageSearch {
  /** Selected edge endpoint A (from). Paired with `b`. */
  a?: string;
  /** Selected edge endpoint B (to). Paired with `a`. */
  b?: string;
}

function validateLineageSearch(
  search: Record<string, unknown>
): LineageSearch {
  const a = search['a'];
  const b = search['b'];
  const out: LineageSearch = {};
  if (typeof a === 'string' && a !== '') out.a = a;
  if (typeof b === 'string' && b !== '') out.b = b;
  // Both keys are required to represent an edge — drop a half-pair so
  // the diff query stays disabled and the panel renders its empty hint.
  if (out.a === undefined || out.b === undefined) {
    return {};
  }
  return out;
}

export const Route = createFileRoute(
  '/orgs/$orgSlug/projects/$projectId/shots/$shotId/lineage/$generationId'
)({
  validateSearch: validateLineageSearch,
  component: LineageRoute,
  errorComponent: LineageRouteError,
});

function LineageRoute(): React.JSX.Element {
  const { orgSlug, projectId, shotId, generationId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const selectedEdge: readonly [string, string] | undefined =
    search.a !== undefined && search.b !== undefined
      ? [search.a, search.b]
      : undefined;

  function onSelectEdge(edge: readonly [string, string] | undefined): void {
    const next: LineageSearch =
      edge === undefined ? {} : { a: edge[0], b: edge[1] };
    void navigate({ search: next, replace: true });
  }

  return (
    <LineageView
      orgSlug={orgSlug}
      projectId={projectId}
      shotId={shotId}
      generationId={generationId}
      selectedEdge={selectedEdge}
      onSelectEdge={onSelectEdge}
    />
  );
}

function LineageRouteError({
  error,
}: {
  error: unknown;
}): React.JSX.Element {
  const isApi = error instanceof ApiError;
  const status = isApi ? error.status : null;
  const heading =
    status === 404
      ? 'Lineage not found'
      : status === 403
        ? 'You don’t have access to this generation'
        : 'Something went wrong loading lineage';
  const body = apiErrorMessage(error, 'Try again, or pick a different generation from the shot page.');

  return (
    <div className="mx-auto max-w-2xl py-7">
      <p className="text-text-faint text-11 mb-2 uppercase tracking-wider">
        Error
      </p>
      <h1 className="text-text text-28 mb-3 font-semibold">{heading}</h1>
      <p className="text-text-muted mb-5 text-13">{body}</p>
      <Link
        to="/"
        className="bg-accent text-accent-fg border-border-strong rounded-2 hover:bg-accent-hi inline-block border px-3 py-2 text-13 transition-colors"
      >
        Back to home
      </Link>
    </div>
  );
}
